/**
 * 자동 진행 확인 — 누르자마자 달리지 않고, 무엇을 어떻게 할지 보여 주고 고치게 한 뒤 시작한다(사용자 요구 2026-10-09).
 * 진행 순서: 2 수집 → 3 진단 → 4 개선안 → 검증 → 해석 → 5 최종안 → 6 리포트. 「시작」 하면 고친 설정을 작업에 저장하고 그대로 진행한다.
 * 가이드라인 원칙 3(진행·중지는 사람이) — 시작 전에 확인하고, 진행 중에는 「자동 진행 중지」 로 멈춘다.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiCpu, FiPlay, FiAlertTriangle } from 'react-icons/fi';
import AppModal from '../common/AppModal';

const SCOPES = ['query', 'index', 'all'];
const PROVIDER_NAME = { claude: 'Claude', codex: 'Codex' };

/**
 * @param initial { parts:{mode,monitorReport,stats,profile}, scope, dataLookup, caseRef, verify, verifyIndex }
 * @param onStart (opts) => void
 */
function AutoRunDialog({ open, initial, connName, connType, provider, onCancel, onStart }) {
  const { t } = useTranslation();
  const [o, setO] = useState(initial);
  useEffect(() => { if (open) setO(initial); }, [open, initial]);
  if (!o) return null;
  const set = (patch) => setO(x => ({ ...x, ...patch }));
  const setParts = (patch) => setO(x => ({ ...x, parts: { ...x.parts, ...patch } }));
  const estimated = o.parts.mode !== 'actual';
  const canVerify = o.verify && !estimated;

  return (
    <AppModal open={open} onClose={onCancel} title={t('common:stn.ar.title')} icon={<FiCpu size={14} />} className="app-modal-default stn-ar-modal">
      <div className="stn-ar-body">
        <div className="stn-note">{t('common:stn.ar.intro')}</div>
        <div className="stn-ar-flow">{t('common:stn.ar.flow')}</div>

        <section className="stn-box">
          <b>{t('common:stn.ar.collect')}</b>
          <div className="stn-row">
            <label><input type="radio" checked={o.parts.mode === 'actual'} onChange={() => setParts({ mode: 'actual' })} /> {t('common:stn.modeActual')}</label>
            <label><input type="radio" checked={o.parts.mode === 'estimated'} onChange={() => setParts({ mode: 'estimated', monitorReport: false })} /> {t('common:stn.modeEstimated')}</label>
            <label title={t('common:stn.monitorHint')}><input type="checkbox" checked={o.parts.monitorReport} disabled={estimated} onChange={e => setParts({ monitorReport: e.target.checked })} /> {t('common:stn.monitorReport')}</label>
          </div>
          <div className="stn-row">
            <label><input type="checkbox" checked={o.parts.stats} onChange={e => setParts({ stats: e.target.checked, profile: e.target.checked ? o.parts.profile : false })} /> {t('common:stn.statsIdx')}</label>
            <label title={t('common:stn.profileHint')}><input type="checkbox" checked={o.parts.profile} disabled={!o.parts.stats} onChange={e => setParts({ profile: e.target.checked })} /> {t('common:stn.ar.profile')}</label>
          </div>
        </section>

        <section className="stn-box">
          <b>{t('common:stn.ar.alternatives')}</b>
          <div className="stn-row">
            <span className="stn-label">{t('common:stn.scope')}</span>
            {SCOPES.map(s => <label key={s}><input type="radio" checked={o.scope === s} onChange={() => set({ scope: s })} /> {t(`common:stn.scopes.${s}`)}</label>)}
          </div>
          <label className="stn-row" title={t('common:stn.dataLookupHint')}>
            <input type="checkbox" checked={o.dataLookup} onChange={e => set({ dataLookup: e.target.checked })} /> {t('common:stn.dataLookup')}
          </label>
          <label className="stn-row" title={t('common:stn.caseRefHint')}>
            <input type="checkbox" checked={o.caseRef} onChange={e => set({ caseRef: e.target.checked })} /> {t('common:stn.caseRef')}
          </label>
        </section>

        <section className="stn-box">
          <b>{t('common:stn.ar.verify')}</b>
          <label className="stn-row"><input type="checkbox" checked={o.verify} onChange={e => set({ verify: e.target.checked })} /> {t('common:stn.ar.verifyAll')}</label>
          <label className="stn-row" title={t('common:stn.ar.verifyIndexHint')}>
            <input type="checkbox" checked={o.verifyIndex} disabled={!o.verify || o.scope === 'query' || connType !== 'dev'} onChange={e => set({ verifyIndex: e.target.checked })} /> {t('common:stn.ar.verifyIndex')}
          </label>
          {estimated && <div className="stn-warn"><FiAlertTriangle size={11} /> {t('common:stn.ar.estimatedNoVerify')}</div>}
          {!canVerify && <div className="stn-note">{t('common:stn.ar.stopAfterAlt')}</div>}
        </section>

        <section className="stn-note stn-ar-info">
          <div>{t('common:stn.ar.ai', { provider: PROVIDER_NAME[provider] || provider || 'AI' })}</div>
          <div>{t('common:stn.ar.conn', { name: connName || '-', type: connType || '-' })}</div>
          {connType === 'prod' && !estimated && <div className="stn-warn"><FiAlertTriangle size={11} /> {t('common:stn.ar.prod')}</div>}
          <div>{t('common:stn.ar.overwrite')}</div>
        </section>

        <div className="stn-actions">
          <button type="button" className="app-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
          <button type="button" className="app-btn app-btn-primary" onClick={() => onStart(o)}><FiPlay size={12} /> {t('common:stn.ar.start')}</button>
        </div>
      </div>
    </AppModal>
  );
}

AutoRunDialog.propTypes = {
  open: PropTypes.bool,
  initial: PropTypes.shape({
    parts: PropTypes.shape({ mode: PropTypes.string, monitorReport: PropTypes.bool, stats: PropTypes.bool, profile: PropTypes.bool }),
    scope: PropTypes.string, dataLookup: PropTypes.bool, caseRef: PropTypes.bool, verify: PropTypes.bool, verifyIndex: PropTypes.bool,
  }),
  connName: PropTypes.string,
  connType: PropTypes.string,
  provider: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
};

export default AutoRunDialog;
